/** Share card: a monochrome 1:1 image of one alert, handed to the system share
 *  sheet (WhatsApp, Status, Messages). WhatsApp is the distribution surface;
 *  every card carries the wordmark and nothing else that could be mistaken for
 *  an official notice. */

import { HAZARDS } from "../../shared/hazards.js";
import type { Alert } from "../../shared/alerts.js";
import { pinMark } from "./marks.js";

const SIZE = 1080;

function svgToImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("svg"));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

function timeLabel(at: number): string {
  return new Date(at).toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" });
}

export async function renderCard(alert: Alert): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;
  const spec = HAZARDS[alert.type];

  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Keyline, as on the app icon.
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 4;
  ctx.strokeRect(40, 40, SIZE - 80, SIZE - 80);

  try {
    // The mark is rendered white on the card's black ground, never inverted.
    const svg = pinMark(alert.type, false).replace(/currentColor/g, "#FFFFFF");
    const img = await svgToImage(svg);
    ctx.drawImage(img, (SIZE - 360) / 2, 150, 360, 360);
  } catch { /* the text still says what it is */ }

  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.font = "800 88px Archivo, system-ui, sans-serif";
  ctx.fillText(spec.label, SIZE / 2, 640);

  ctx.fillStyle = "#B0B0B0";
  ctx.font = "400 40px Inter, system-ui, sans-serif";
  const src = alert.source === "bridge" ? "nga grupet" : alert.source === "osm" ? "nga harta" : "nga shoferët";
  const who = alert.samples > 1 ? `${alert.samples} shoferë` : src;
  ctx.fillText(`${timeLabel(alert.createdAt)} · ${who}`, SIZE / 2, 720);

  ctx.font = "400 34px Inter, system-ui, sans-serif";
  ctx.fillText("Ngadalëso. Shoferi para teje e ka parë.", SIZE / 2, 800);

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 56px Archivo, system-ui, sans-serif";
  ctx.fillText("fanar", SIZE / 2, 960);
  ctx.fillStyle = "#B0B0B0";
  ctx.font = "400 30px Inter, system-ui, sans-serif";
  ctx.fillText(location.host, SIZE / 2, 1010);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

export async function shareAlert(alert: Alert): Promise<boolean> {
  const spec = HAZARDS[alert.type];
  const text = `${spec.label}, ${timeLabel(alert.createdAt)}. Ngadalëso. — fanar`;
  const url = location.origin;
  try {
    const blob = await renderCard(alert);
    if (blob !== null) {
      const file = new File([blob], "fanar.png", { type: "image/png" });
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
      if (typeof nav.canShare === "function" && nav.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, url });
        return true;
      }
    }
    await navigator.share({ text, url });
    return true;
  } catch {
    return false; // the user closed the sheet, or sharing is unavailable
  }
}
